import { createSlice } from "@reduxjs/toolkit";
import {
  getChallenges,
  getMoreChallenges,
  getChallengeCategories,
  getChallengeSettings,
  subscribeToChallenge,
  unsubscribeFromChallenge,
  recordChallengeActivity,
  pinChallenge,
  unpinChallenge,
} from "../actions/challengesActions";

const initialState = {
  list: {
    isLoading: false,
    isLoadingMore: false,
    error: null,
    data: [],
    meta: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
  },
  categories: { isLoading: false, error: null, data: [] },
  filters: { tab: "all", categoryId: null, term: "" },
  subscription: { isLoading: false, error: null, success: false, challengeId: null },
  activity: { isLoading: false, error: null, success: false, result: null, challengeId: null },
  pin: { isLoading: false, error: null, challengeId: null },
  settings: { isLoading: false, error: null, data: null },
};

const updateChallengeLikedStatus = (state, challengeId, liked) => {
  const index = state.list.data.findIndex((c) => c.id === challengeId);
  if (index >= 0) state.list.data[index].attributes.liked = liked;
};

// After a confirm-activity round-trip succeeds, mirror the server-side state
// changes onto the locally cached challenge so the card re-renders correctly
// without waiting for the next list refetch:
//   - `can_create_activity = false` → CTA disables until the next day's window.
//   - On the final-day confirm the server returns the updated subscription
//     resource with `completed_at` populated; copy it onto the participation
//     so ChallengeCard.getStatus() flips to "conclusa", disabling the CTA
//     permanently and hiding the bonus hint.
const updateChallengeActivityStatus = (state, payload) => {
  const challengeId = payload?.challengeId;
  const index = state.list.data.findIndex((c) => c.id === challengeId);
  if (index < 0) return;

  const participation = state.list.data[index].attributes.participation;
  if (!participation) return;

  participation.attributes.can_create_activity = false;
  // The just-confirmed day is no longer pending. Clearing this in lockstep
  // with can_create_activity prevents any UI path from re-displaying the
  // already-confirmed date in the "Conferma attività" prompt.
  participation.attributes.pending_activity_date = null;

  const completedAt = payload?.subscription?.data?.attributes?.completed_at
    ?? payload?.subscription?.attributes?.completed_at
    ?? null;
  if (completedAt) {
    participation.attributes.completed_at = completedAt;
  }
};

const challengesSlice = createSlice({
  name: "challenges",
  initialState,
  reducers: {
    setTab: (state, action) => {
      state.filters.tab = action.payload;
    },
    setCategoryFilter: (state, action) => {
      state.filters.categoryId = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.filters.term = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    resetSubscriptionState: (state) => {
      state.subscription = initialState.subscription;
    },
    resetActivityState: (state) => {
      state.activity = initialState.activity;
    },
    clearChallengeList: (state) => {
      state.list.data = [];
      state.list.meta = initialState.list.meta;
      state.list.error = null;
    },
    toggleChallengeLiked: (state, action) => {
      const { challengeId } = action.payload;
      const index = state.list.data.findIndex((c) => c.id === challengeId);
      if (index >= 0) {
        state.list.data[index].attributes.liked = !state.list.data[index].attributes.liked;
      }
    },
  },
  extraReducers: (builder) => {
    // GET CHALLENGES
    builder.addCase(getChallenges.pending, (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    });
    builder.addCase(getChallenges.fulfilled, (state, action) => {
      state.list.isLoading = false;
      state.list.data = action.payload.data || [];
      state.list.meta = action.payload.meta || initialState.list.meta;
    });
    builder.addCase(getChallenges.rejected, (state, action) => {
      state.list.isLoading = false;
      state.list.error = action.error.message;
    });

    // GET MORE CHALLENGES
    builder.addCase(getMoreChallenges.pending, (state) => {
      state.list.isLoadingMore = true;
    });
    builder.addCase(getMoreChallenges.fulfilled, (state, action) => {
      state.list.isLoadingMore = false;
      state.list.data = [...state.list.data, ...(action.payload.data || [])];
      state.list.meta = action.payload.meta || state.list.meta;
    });
    builder.addCase(getMoreChallenges.rejected, (state, action) => {
      state.list.isLoadingMore = false;
      state.list.error = action.error.message;
    });

    // GET CATEGORIES
    builder.addCase(getChallengeCategories.pending, (state) => {
      state.categories.isLoading = true;
    });
    builder.addCase(getChallengeCategories.fulfilled, (state, action) => {
      state.categories.isLoading = false;
      state.categories.data = action.payload || [];
    });
    builder.addCase(getChallengeCategories.rejected, (state, action) => {
      state.categories.isLoading = false;
      state.categories.error = action.error.message;
    });

    // GET SETTINGS
    builder.addCase(getChallengeSettings.pending, (state) => {
      state.settings.isLoading = true;
      state.settings.error = null;
    });
    builder.addCase(getChallengeSettings.fulfilled, (state, action) => {
      state.settings.isLoading = false;
      state.settings.data = action.payload?.attributes || null;
    });
    builder.addCase(getChallengeSettings.rejected, (state, action) => {
      state.settings.isLoading = false;
      state.settings.error = action.error.message;
    });

    // SUBSCRIBE
    builder.addCase(subscribeToChallenge.pending, (state, action) => {
      state.subscription.isLoading = true;
      state.subscription.error = null;
      state.subscription.success = false;
      state.subscription.challengeId = action.meta.arg.challengeId;
    });
    builder.addCase(subscribeToChallenge.fulfilled, (state) => {
      state.subscription.isLoading = false;
      state.subscription.success = true;
    });
    builder.addCase(subscribeToChallenge.rejected, (state, action) => {
      state.subscription.isLoading = false;
      state.subscription.error = action.error.message;
    });

    // UNSUBSCRIBE
    builder.addCase(unsubscribeFromChallenge.pending, (state, action) => {
      state.subscription.isLoading = true;
      state.subscription.challengeId = action.meta.arg.challengeId;
    });
    builder.addCase(unsubscribeFromChallenge.fulfilled, (state, action) => {
      state.subscription.isLoading = false;
      state.subscription.success = true;
      const index = state.list.data.findIndex((c) => c.id === action.payload.challengeId);
      if (index >= 0) state.list.data[index].attributes.participation = null;
    });
    builder.addCase(unsubscribeFromChallenge.rejected, (state, action) => {
      state.subscription.isLoading = false;
      state.subscription.error = action.error.message;
    });

    // RECORD ACTIVITY
    builder.addCase(recordChallengeActivity.pending, (state, action) => {
      state.activity.isLoading = true;
      state.activity.error = null;
      state.activity.success = false;
      state.activity.challengeId = action.meta.arg.challengeId;
    });
    builder.addCase(recordChallengeActivity.fulfilled, (state, action) => {
      state.activity.isLoading = false;
      state.activity.success = true;
      state.activity.result = action.payload;
      updateChallengeActivityStatus(state, action.payload);
    });
    builder.addCase(recordChallengeActivity.rejected, (state, action) => {
      state.activity.isLoading = false;
      state.activity.error = action.payload || action.error.message;
    });

    // PIN (with optimistic update)
    builder.addCase(pinChallenge.pending, (state, action) => {
      state.pin.isLoading = true;
      state.pin.challengeId = action.meta.arg.challengeId;
      updateChallengeLikedStatus(state, action.meta.arg.challengeId, true);
    });
    builder.addCase(pinChallenge.fulfilled, (state) => {
      state.pin.isLoading = false;
      state.pin.challengeId = null;
    });
    builder.addCase(pinChallenge.rejected, (state, action) => {
      state.pin.isLoading = false;
      state.pin.error = action.error.message;
      updateChallengeLikedStatus(state, action.meta.arg.challengeId, false);
    });

    // UNPIN (with optimistic update)
    builder.addCase(unpinChallenge.pending, (state, action) => {
      state.pin.isLoading = true;
      state.pin.challengeId = action.meta.arg.challengeId;
      updateChallengeLikedStatus(state, action.meta.arg.challengeId, false);
    });
    builder.addCase(unpinChallenge.fulfilled, (state) => {
      state.pin.isLoading = false;
      state.pin.challengeId = null;
    });
    builder.addCase(unpinChallenge.rejected, (state, action) => {
      state.pin.isLoading = false;
      state.pin.error = action.error.message;
      updateChallengeLikedStatus(state, action.meta.arg.challengeId, true);
    });
  },
});

export const {
  setTab,
  setCategoryFilter,
  setSearchTerm,
  clearFilters,
  resetSubscriptionState,
  resetActivityState,
  clearChallengeList,
  toggleChallengeLiked,
} = challengesSlice.actions;

export default challengesSlice.reducer;
